import type { ReactNode } from "react";

/** [подпись](https://…) и голые https://-ссылки */
const LINK_PATTERN =
  /\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s<>"')\]]+)/g;

const INLINE_MD_PATTERN =
  /\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s<>"')\]]+)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;

const ORDERED_LIST_LINE = /^\d+\.\s+/;

function isSafeHref(href: string): boolean {
  try {
    const u = new URL(href.trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

/** Текст для превью цитаты — без markdown-разметки */
export function messageTextPlain(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^\d+\.\s+/gm, "");
}

function formatLinksOnly(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;

  for (const match of text.matchAll(LINK_PATTERN)) {
    const index = match.index ?? 0;
    if (index > last) {
      nodes.push(text.slice(last, index));
    }

    const mdLabel = match[1];
    const mdHref = match[2];
    const bareUrl = match[3];

    if (mdLabel != null && mdHref != null) {
      const href = mdHref.trim();
      if (isSafeHref(href)) {
        nodes.push(
          <a
            key={key++}
            className="message-link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {mdLabel}
          </a>
        );
      } else {
        nodes.push(match[0]);
      }
    } else if (bareUrl) {
      const href = bareUrl.trim();
      if (isSafeHref(href)) {
        nodes.push(
          <a
            key={key++}
            className="message-link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {href}
          </a>
        );
      } else {
        nodes.push(bareUrl);
      }
    }

    last = index + match[0].length;
  }

  if (last < text.length) {
    nodes.push(text.slice(last));
  }

  return nodes.length > 0 ? nodes : [text];
}

function formatInlineMarkdown(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;

  for (const match of text.matchAll(INLINE_MD_PATTERN)) {
    const index = match.index ?? 0;
    if (index > last) {
      nodes.push(text.slice(last, index));
    }

    const mdLabel = match[1];
    const mdHref = match[2];
    const bareUrl = match[3];
    const bold = match[4];
    const italic = match[5];
    const k = `${keyPrefix}-${key++}`;

    if (mdLabel != null && mdHref != null) {
      const href = mdHref.trim();
      if (isSafeHref(href)) {
        nodes.push(
          <a
            key={k}
            className="message-link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {mdLabel}
          </a>
        );
      } else {
        nodes.push(match[0]);
      }
    } else if (bareUrl) {
      const href = bareUrl.trim();
      if (isSafeHref(href)) {
        nodes.push(
          <a
            key={k}
            className="message-link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {href}
          </a>
        );
      } else {
        nodes.push(bareUrl);
      }
    } else if (bold != null) {
      nodes.push(<strong key={k}>{bold}</strong>);
    } else if (italic != null) {
      nodes.push(<em key={k}>{italic}</em>);
    }

    last = index + match[0].length;
  }

  if (last < text.length) {
    nodes.push(text.slice(last));
  }

  return nodes.length > 0 ? nodes : [text];
}

function isOrderedListBlock(block: string): boolean {
  const lines = block.split("\n").filter((l) => l.trim() !== "");
  return lines.length > 0 && lines.every((l) => ORDERED_LIST_LINE.test(l.trim()));
}

function formatMarkdownBlocks(text: string): ReactNode[] {
  const normalized = text.replace(/  \n/g, "\n").replace(/\r\n/g, "\n");
  const blocks = normalized.split(/\n\n+/);
  const nodes: ReactNode[] = [];

  blocks.forEach((block, bi) => {
    const trimmed = block.trim();
    if (!trimmed) return;

    if (isOrderedListBlock(trimmed)) {
      const items = trimmed
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => l.replace(ORDERED_LIST_LINE, ""));
      nodes.push(
        <ol key={`b${bi}`} className="message-md-list">
          {items.map((item, ii) => (
            <li key={ii}>{formatInlineMarkdown(item, `b${bi}-i${ii}`)}</li>
          ))}
        </ol>
      );
      return;
    }

    const lines = trimmed.split("\n");
    nodes.push(
      <p key={`b${bi}`} className="message-md-p">
        {lines.map((line, li) => (
          <span key={li}>
            {li > 0 ? <br /> : null}
            {formatInlineMarkdown(line, `b${bi}-l${li}`)}
          </span>
        ))}
      </p>
    );
  });

  return nodes.length > 0 ? nodes : formatLinksOnly(text);
}

export type FormatMessageOptions = {
  /** **жирный**, списки 1. …, абзацы по пустой строке */
  markdown?: boolean;
};

export function formatMessageText(
  text: string,
  options?: FormatMessageOptions
): ReactNode {
  if (options?.markdown) {
    return <>{formatMarkdownBlocks(text)}</>;
  }
  const nodes = formatLinksOnly(text);
  return <>{nodes}</>;
}
